import { Flex, Text } from "@chakra-ui/react";

import {
  useTermsDispatch,
  useTermsSelector,
  useLessonDispatch,
  useLessonSelector,
  useUserSelector
} from "@context/hooks";
import { Button, Icon, NavigationIcons, getTrackingId } from "@euka-mfes/bark";
import { LearningClassEnums as CLS } from "@/ts/enums/LearningClassEnums";
import { useLocation, useNavigate } from "react-router-dom";
import { getWeekRoute } from "@utils/route";
import { getCurrentSectionFromPath } from "@/utils/lessonUtils";
import { getPlpGradeLabel } from "@utils/productFamilyGrades";

interface LessonHeaderBreadcrumbProps {
  textColor: string;
}

const LessonHeaderBreadcrumb = ({ textColor }: LessonHeaderBreadcrumbProps) => {
  const { selectedTerm, selectedWeek } = useTermsSelector();

  const {
    selectedLesson,
    selectedSubject,
    hasAttemptedApplyQuestion,
    isApplyResultViewShown
  } = useLessonSelector();
  const {
    setShowBlockNavigationModal,
    setPendingNavigationPath,
    setHasAttemptedApplyQuestion,
    resetLessonState
  } = useLessonDispatch();

  const { selectedGrade } = useUserSelector();
  const { resetTermsDetails } = useTermsDispatch();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const currentSection = getCurrentSectionFromPath(pathname);

  const handleGoBack = () => {
    const target = getWeekRoute(
      selectedGrade?.grade_code as number,
      selectedTerm?.id as string,
      selectedWeek?.id as string
    );

    if (currentSection === "apply") {
      if (hasAttemptedApplyQuestion && !isApplyResultViewShown) {
        setPendingNavigationPath(target);
        setShowBlockNavigationModal(true);
        return;
      }
      // No attempts yet, navigate and reset flag
      setHasAttemptedApplyQuestion(false);
    }

    if (currentSection === "summary") {
      resetLessonState();
      resetTermsDetails();
    }

    navigate(target);
  };

  return (
    <Flex
      align="center"
      justify="flex-start"
      gap={4}
      bg="transparent"
      border="none"
      transition="all 0.2s ease"
      data-testid="header-breadcrumb-back-button"
    >
      <Button
        data-testid="lesson-back-button"
        onClick={handleGoBack}
        variant="icon"
        aspectRatio="1/1"
        border="none"
        _hover={{ backgroundColor: "transparent" }}
        p="4"
        data-tracking-id={getTrackingId(
          [CLS.LessonHeaderBreadcrumb, CLS.Button],
          "back"
        )}
      >
        <Icon
          icon={<NavigationIcons.ArrowLeft size="xl" />}
          fillColor={textColor}
        />
      </Button>
      <Flex
        display={{ base: "none", md: "flex" }}
        flexDirection="column"
        alignItems="flex-start"
        gap={1}
      >
        <Text textStyle="tag.small" color={textColor}>
          {selectedTerm?.name} / {selectedWeek?.name} / {selectedSubject?.name}
          {getPlpGradeLabel(selectedSubject?.plp_grade_name ?? "")} / Lesson{" "}
          {selectedLesson?.id}
        </Text>
        <Text as="h1" textStyle="title.5" color={textColor}>
          {selectedLesson?.description}
        </Text>
      </Flex>
    </Flex>
  );
};

export default LessonHeaderBreadcrumb;
